import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { resendEmailConfirmation } from '@api/user'
import { EMAIL_VERIFICATION_CODE, GROUP_LIMIT_CODE } from '@common/apiErrorCodes'

/**
 * Reusable hook for handling email verification and group limit errors
 * from API responses (403 with code 1004 or 1007).
 *
 * Used by comments, group creation, group track posting, and profile editing.
 */
const useEmailVerification = () => {
  const [emailVerificationError, setEmailVerificationError] = useState(false)
  const [groupLimitError, setGroupLimitError] = useState(false)
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [sendingVerificationEmail, setSendingVerificationEmail] = useState(false)

  const clearErrors = () => {
    setEmailVerificationError(false)
    setGroupLimitError(false)
  }

  /**
   * Returns true if the error was handled (verification or group limit),
   * false if it was not recognized and should be handled by the caller.
   */
  const handleApiError = (error) => {
    const status = error?.response?.status
    const code = error?.response?.data?.code

    if (status === 403 && code === EMAIL_VERIFICATION_CODE) {
      setEmailVerificationError(true)
      return true
    }
    if (status === 403 && code === GROUP_LIMIT_CODE) {
      setGroupLimitError(true)
      return true
    }
    return false
  }

  const handleResendVerificationEmail = () => {
    setSendingVerificationEmail(true)
    resendEmailConfirmation()
      .then(() => {
        setVerificationEmailSent(true)
      })
      .catch((error) => {
        Sentry.captureException(error)
      })
      .finally(() => {
        setSendingVerificationEmail(false)
      })
  }

  return {
    emailVerificationError,
    groupLimitError,
    verificationEmailSent,
    sendingVerificationEmail,
    clearErrors,
    handleApiError,
    handleResendVerificationEmail,
  }
}

export default useEmailVerification
